import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import { Typography } from '@mui/material';

import NumberTypo from 'src/components/common/Number';
import Tooltip from 'src/components/common/Tooltip';
import GradientButton from 'src/components/GradientButton';
import IconButton from 'src/components/IconButton';
import Image from 'src/components/Image';
import { Base02Title } from 'src/components/ReverseGradientButton/styled';
import TransactionBottomSheet from 'src/components/TransactionBottomSheet';
import { DYDX_TOKEN } from 'src/constants/tokens';
import { COSMOSTATION_OPERATOR_ADDRESS } from 'src/constants/validator';
import { useAccountSWR } from 'src/hooks/SWR/cosmos/useAccountSWR';
import { useActiveValidatorsSWR } from 'src/hooks/SWR/cosmos/useActiveValidatorsSWR';
import { useCosmosBalanceSWR } from 'src/hooks/SWR/cosmos/useCosmosBalanceSWR';
import { useDelegationSWR } from 'src/hooks/SWR/cosmos/useDelegationSWR';
import { useSimulateSWR } from 'src/hooks/SWR/cosmos/useSimulateSWR';
import { useTxInfoSWR } from 'src/hooks/SWR/cosmos/useTxInfoSWR';
import { useValidatorsSWR } from 'src/hooks/SWR/cosmos/useValidatorsSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import type { Validator } from 'src/types/validator';
import { getReDelegateDirectDoc, getSimulateRedelegateTxBytes, signDirectTx } from 'src/utils/cosmos';
import { mnemonicToPair } from 'src/utils/crypto';
import { pageView, restakingClickEvent } from 'src/utils/ga';
import { ceil, fix, gt, isDecimal, times, toBaseDenomAmount, toDisplayDenomAmount, trimZeros } from 'src/utils/number';
import { isEqualsIgnoringCase } from 'src/utils/string';

import {
  AmountPresetButton,
  Container,
  ContentsContainer,
  DetailContainer,
  DownArrowIconContainer,
  DYDXBodyContainer,
  DYDXContainer,
  DYDXIconContainer,
  DYDXTopContainer,
  DYDXTopLeftAmountContainer,
  DYDXTopLeftContainer,
  DYDXTopRightContainer,
  Header,
  HeaderTitle,
  ImageContainer,
  LeftContainer,
  LeftTextContainer,
  RightContainer,
  StyledBottomSheet,
  StyledInput,
  TokenContainer,
  TokenRightContainer,
  TokenTextContainer,
  ValidatorButton,
  ValidatorSelectionContainer,
  WrapperContainer,
} from './styled';
import { Base02TextContainer } from '../../DefaultStaking/styled';
import SignConfirmBottomSheet from '../../SignConfirmBottomSheet';
import TxDetails from '../../TxDetails';
import ValidatorBottomSheet from '../../ValidatorBottomSheet';

import { ReactComponent as CloseIcon } from 'src/assets/svg/Close.svg';
import DefaultValidatorIcon from 'src/assets/svg/DefaultValidator.svg';
import { ReactComponent as DownArrowIcon } from 'src/assets/svg/DownArrorIcon.svg';
import { ReactComponent as DYDXIcon } from 'src/assets/svg/DYDX.svg';
import { ReactComponent as RightChevronIcon } from 'src/assets/svg/RightChevron.svg';

type RestakeBottomSheetProps = Omit<React.ComponentProps<typeof StyledBottomSheet>, 'children'> & {
  selectedValidatorAddress: string;
};

export default function RestakeBottomSheet({ selectedValidatorAddress, onClose, ...remainder }: RestakeBottomSheetProps) {
  const { enqueueSnackbar } = useSnackbar();

  const { currentAccount } = useCurrentAccount();

  const [currentDisplayAmount, setCurrentDisplayAmount] = useState('');

  const [isDisabled, setIsDisabled] = useState(false);
  const [isOpenedFromValidatorBottomSheet, setIsOpenedFromValidatorBottomsheet] = useState(false);
  const [isOpenedToValidatorBottomSheet, setIsOpenedToValidatorBottomsheet] = useState(false);

  const [isOpenSignConfirmBottomsheet, setIsOpenSignConfirmBottomsheet] = useState(false);
  const [isOpenTrasactionBottomsheet, setIsOpenTrasactionBottomsheet] = useState(false);

  const [isTxTrackingFailed, setIsTxTrackingFailed] = useState(false);
  const [isTxTrackingSuccess, setIsTxTrackingSucces] = useState(false);

  const [currentMemo, setCurrentMemo] = useState('');

  const [transactionHash, setTransactionHash] = useState('');

  const [debouncedCurrentDisplayAmount] = useDebounce(currentDisplayAmount, 500);
  const [debouncedCurrentMemo] = useDebounce(currentMemo, 500);

  const [currentFromValidatorAddress, setCurrentFromValidatorAddress] = useState('');
  const [currentToValidatorAddress, setCurrentToValidatorAddress] = useState('');

  const validators = useValidatorsSWR();

  const currentFromValidator = useMemo(
    () => validators.data?.validators.find((item) => isEqualsIgnoringCase(item.operator_address, currentFromValidatorAddress || selectedValidatorAddress)),
    [currentFromValidatorAddress, selectedValidatorAddress, validators.data?.validators],
  );

  const currentToValidator = useMemo(
    () => validators.data?.validators.find((item) => isEqualsIgnoringCase(item.operator_address, currentToValidatorAddress || COSMOSTATION_OPERATOR_ADDRESS)),
    [currentToValidatorAddress, validators.data?.validators],
  );

  const delegation = useDelegationSWR({ cosmosAddress: currentAccount.cosmosAddress });

  const delegatedValidators = useMemo(
    () =>
      (delegation.data?.delegation_responses
        ?.map((item) => validators.data?.validators.find((validator) => isEqualsIgnoringCase(validator.operator_address, item.delegation.validator_address)))
        .filter((item) => !!item) as Validator[]) || [],
    [delegation.data?.delegation_responses, validators.data?.validators],
  );

  const orderedFromValidators = useMemo(
    () =>
      delegatedValidators
        .sort((a, b) => b.description.moniker.toLowerCase().trim().localeCompare(a.description.moniker.toLowerCase().trim()))
        .sort((a, b) => {
          if (!/^[A-Za-z0-9]/.test(b.description.moniker.toLowerCase().trim()[0]) && /^[A-Za-z0-9]/.test(a.description.moniker.toLowerCase().trim()[0])) {
            return 1;
          }
          if (/^[A-Za-z0-9]/.test(b.description.moniker.toLowerCase().trim()[0]) && !/^[A-Za-z0-9]/.test(a.description.moniker.toLowerCase().trim()[0])) {
            return -1;
          }

          return 0;
        })
        .sort((a, b) => (gt(a.commission.commission_rates.rate, b.commission.commission_rates.rate) ? 1 : -1))
        .sort((item) => (isEqualsIgnoringCase(item.operator_address, COSMOSTATION_OPERATOR_ADDRESS) ? -1 : 1)) || [],
    [delegatedValidators],
  );

  const activeValidators = useActiveValidatorsSWR();

  const orderedToValidators = useMemo(
    () =>
      activeValidators.data?.validators
        .sort((a, b) => b.description.moniker.toLowerCase().trim().localeCompare(a.description.moniker.toLowerCase().trim()))
        .sort((a, b) => {
          if (!/^[A-Za-z0-9]/.test(b.description.moniker.toLowerCase().trim()[0]) && /^[A-Za-z0-9]/.test(a.description.moniker.toLowerCase().trim()[0])) {
            return 1;
          }
          if (/^[A-Za-z0-9]/.test(b.description.moniker.toLowerCase().trim()[0]) && !/^[A-Za-z0-9]/.test(a.description.moniker.toLowerCase().trim()[0])) {
            return -1;
          }
          return 0;
        })
        .sort((a, b) => (gt(a.commission.commission_rates.rate, b.commission.commission_rates.rate) ? 1 : -1))
        .sort((item) => (isEqualsIgnoringCase(item.operator_address, COSMOSTATION_OPERATOR_ADDRESS) ? -1 : 1)) || [],
    [activeValidators.data?.validators],
  );

  const baseDebouncedCurrentDisplayAmount = useMemo(
    () => toBaseDenomAmount(debouncedCurrentDisplayAmount || '0', DYDX_TOKEN.decimals),
    [debouncedCurrentDisplayAmount],
  );

  const balance = useCosmosBalanceSWR(currentAccount.cosmosAddress);

  const baseCoinAmount = useMemo(() => balance.data?.balances?.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0', [balance.data?.balances]);

  const restakableBaseAmount = useMemo(
    () =>
      delegation.data?.delegation_responses?.find((item) => isEqualsIgnoringCase(item.delegation.validator_address, currentFromValidator?.operator_address))
        ?.balance.amount || '0',
    [delegation.data?.delegation_responses, currentFromValidator?.operator_address],
  );

  const account = useAccountSWR(currentAccount.cosmosAddress);

  const simulateDirectTxBytes = useMemo(
    () =>
      account.data?.account.account_number &&
      currentFromValidator?.operator_address &&
      currentToValidator?.operator_address &&
      currentAccount.mnemonic &&
      account.data?.account.sequence &&
      gt(debouncedCurrentDisplayAmount, '0')
        ? getSimulateRedelegateTxBytes({
            accountNumber: account.data.account.account_number,
            sequence: account.data.account.sequence,
            mnemonic: currentAccount.mnemonic,
            delegatorAddress: currentAccount.cosmosAddress,
            srcValidatorAddress: currentFromValidator.operator_address,
            dstValidatorAddress: currentToValidator.operator_address,
            redelegateAmount: toBaseDenomAmount(debouncedCurrentDisplayAmount, DYDX_TOKEN.decimals),
            memo: debouncedCurrentMemo,
          })
        : undefined,
    [
      account.data?.account.account_number,
      account.data?.account.sequence,
      currentAccount.cosmosAddress,
      currentAccount.mnemonic,
      currentFromValidator?.operator_address,
      currentToValidator?.operator_address,
      debouncedCurrentDisplayAmount,
      debouncedCurrentMemo,
    ],
  );

  const simulate = useSimulateSWR({ txBytes: simulateDirectTxBytes?.tx_bytes });

  const simulatedGas = useMemo(
    () => (simulate.data?.gas_info?.gas_used ? String(ceil(times(simulate.data.gas_info.gas_used, '1.2'))) : undefined),
    [simulate.data?.gas_info?.gas_used],
  );

  const currentGas = useMemo(() => simulatedGas || '380000', [simulatedGas]);

  const baseEstimateFeeAmount = useMemo(() => String(ceil(times(currentGas, '12500000000'))), [currentGas]);

  const displayEstimateFeeAmount = useMemo(() => toDisplayDenomAmount(baseEstimateFeeAmount, DYDX_TOKEN.decimals), [baseEstimateFeeAmount]);

  const redelegateDirectTx = useMemo(
    () =>
      account.data?.account.account_number &&
      currentFromValidator?.operator_address &&
      currentToValidator?.operator_address &&
      currentAccount.mnemonic &&
      account.data?.account.sequence &&
      currentAccount.cosmosAddress &&
      baseEstimateFeeAmount &&
      gt(baseDebouncedCurrentDisplayAmount, '0') &&
      currentGas
        ? getReDelegateDirectDoc({
            accountNumber: account.data.account.account_number,
            sequence: account.data.account.sequence,
            mnemonic: currentAccount.mnemonic,
            delegatorAddress: currentAccount.cosmosAddress,
            srcValidatorAddress: currentFromValidator.operator_address,
            dstValidatorAddress: currentToValidator.operator_address,
            redelegateAmount: baseDebouncedCurrentDisplayAmount,
            simulatedGas: currentGas,
            simulatedFeeAmount: baseEstimateFeeAmount,
            memo: debouncedCurrentMemo,
          })
        : undefined,
    [
      account.data?.account.account_number,
      account.data?.account.sequence,
      baseDebouncedCurrentDisplayAmount,
      baseEstimateFeeAmount,
      currentAccount.cosmosAddress,
      currentAccount.mnemonic,
      currentFromValidator?.operator_address,
      currentGas,
      currentToValidator?.operator_address,
      debouncedCurrentMemo,
    ],
  );

  const txInfo = useTxInfoSWR(transactionHash);

  const transactionBodyText = useMemo(() => {
    if (isTxTrackingFailed) return 'Switching Transaction failed.';

    if (isTxTrackingSuccess) return 'Switching Transaction succeeded.';

    return `Redelegated ${gt(debouncedCurrentDisplayAmount, '0.000001') ? debouncedCurrentDisplayAmount : '< 0.000001'} DYDX to ${
      currentToValidator?.description.moniker || ''
    }.`;
  }, [currentToValidator?.description.moniker, debouncedCurrentDisplayAmount, isTxTrackingFailed, isTxTrackingSuccess]);

  // NOTE: This is not used
  // const transactionRedirectText = useMemo(() => {
  //   if (isTxTrackingSuccess) return 'Guide';

  //   return '';
  // }, [isTxTrackingSuccess]);

  // const transactionSubText = useMemo(
  //   () => (isTxTrackingSuccess ? 'To manage your staked assets, follow the guide and export your secret phrase to Cosmostation Wallet.' : undefined),
  //   [isTxTrackingSuccess],
  // );

  const errorMessage = useMemo(() => {
    if (!currentAccount.cosmosAddress) {
      return 'Please connect wallet';
    }

    if (!currentFromValidator || !currentToValidator) {
      return 'Please select validator';
    }

    if (isEqualsIgnoringCase(currentFromValidator.operator_address, currentToValidator.operator_address)) {
      return 'Please select different Restake validator';
    }

    if (!currentDisplayAmount) {
      return 'Please input amount';
    }

    if (gt(baseEstimateFeeAmount, baseCoinAmount)) {
      return 'Insufficient DYDX balance for transaction fee';
    }

    if (gt(debouncedCurrentDisplayAmount, toDisplayDenomAmount(restakableBaseAmount, DYDX_TOKEN.decimals))) {
      return 'You cannot stake more than the currently staked amount.';
    }

    if (!redelegateDirectTx) {
      return 'Invalid Transaction';
    }

    return '';
  }, [
    baseCoinAmount,
    baseEstimateFeeAmount,
    currentAccount.cosmosAddress,
    currentDisplayAmount,
    currentFromValidator,
    currentToValidator,
    debouncedCurrentDisplayAmount,
    redelegateDirectTx,
    restakableBaseAmount,
  ]);

  const txConfirmedStatus = useMemo(() => {
    if (txInfo.error?.response?.status && txInfo.error.response.status >= 400 && txInfo.error.response.status < 500) return 'pending';

    if (txInfo.data?.tx_response.code !== undefined) {
      if (txInfo.data.tx_response.code !== 0) return 'failed';

      if (txInfo.data.tx_response.code === 0) return 'confirmed';
    }

    return undefined;
  }, [txInfo.data?.tx_response.code, txInfo.error?.response?.status]);

  useEffect(() => {
    pageView('Switch-Validator', 'Switch-Validator');
  }, []);

  const debouncedEnabled = useDebouncedCallback(() => {
    setTimeout(() => {
      setIsDisabled(false);
    }, 700);
  }, 700);

  useEffect(() => {
    setIsDisabled(true);

    debouncedEnabled();
  }, [debouncedEnabled, redelegateDirectTx, currentMemo]);

  useEffect(() => {
    if (isOpenTrasactionBottomsheet) {
      if (txConfirmedStatus === 'confirmed') {
        setIsTxTrackingSucces(true);
      }
      if (txConfirmedStatus === 'failed' || txInfo.hasTimedOut) {
        setIsTxTrackingFailed(true);
      }
    }
  }, [isOpenTrasactionBottomsheet, txConfirmedStatus, txInfo.hasTimedOut]);

  useEffect(() => {
    if (!currentAccount.cosmosAddress) {
      setIsOpenTrasactionBottomsheet(false);
      setIsTxTrackingFailed(false);
      setIsTxTrackingSucces(false);
      setTransactionHash('');
      setCurrentDisplayAmount('');
      setCurrentFromValidatorAddress('');
      setCurrentToValidatorAddress('');
      setCurrentMemo('');
      onClose?.({}, 'backdropClick');
    }
  }, [currentAccount.cosmosAddress, onClose]);

  return (
    <StyledBottomSheet {...remainder} onClose={onClose}>
      <Container>
        <>
          <ContentsContainer>
            <Header>
              <HeaderTitle>
                <Typography variant="h2">Switch Validator</Typography>
              </HeaderTitle>
              <IconButton
                onClick={() => {
                  setCurrentDisplayAmount('');
                  setCurrentFromValidatorAddress('');
                  setCurrentToValidatorAddress('');
                  onClose?.({}, 'backdropClick');
                }}
              >
                <CloseIcon />
              </IconButton>
            </Header>

            <ValidatorSelectionContainer>
              <ValidatorButton
                onClick={() => {
                  setIsOpenedFromValidatorBottomsheet(true);
                }}
              >
                <LeftContainer>
                  <ImageContainer>
                    <Image
                      src={`https://raw.githubusercontent.com/cosmostation/chainlist/main/chain/dydx/moniker/${
                        currentFromValidator?.operator_address || ''
                      }.png`}
                      defaultImgSrc={DefaultValidatorIcon}
                    />
                  </ImageContainer>

                  <LeftTextContainer>
                    <Typography variant="h3">{currentFromValidator?.description.moniker}</Typography>

                    <Base02Title>
                      <Typography variant="h8">From Validator</Typography>
                    </Base02Title>
                  </LeftTextContainer>
                </LeftContainer>

                <RightContainer>
                  <RightChevronIcon />
                </RightContainer>

                <DownArrowIconContainer>
                  <DownArrowIcon />
                </DownArrowIconContainer>
              </ValidatorButton>

              <ValidatorButton
                onClick={() => {
                  setIsOpenedToValidatorBottomsheet(true);
                }}
              >
                <LeftContainer>
                  <ImageContainer>
                    <Image
                      src={`https://raw.githubusercontent.com/cosmostation/chainlist/main/chain/dydx/moniker/${currentToValidator?.operator_address || ''}.png`}
                      defaultImgSrc={DefaultValidatorIcon}
                    />
                  </ImageContainer>
                  <LeftTextContainer>
                    <Typography variant="h3">{currentToValidator?.description.moniker}</Typography>

                    <Base02Title>
                      <Typography variant="h8">To Validator</Typography>
                    </Base02Title>
                  </LeftTextContainer>
                </LeftContainer>

                <RightContainer>
                  <RightChevronIcon />
                </RightContainer>
              </ValidatorButton>
            </ValidatorSelectionContainer>

            <DYDXContainer>
              <DYDXTopContainer>
                <DYDXTopLeftContainer>
                  <Typography variant="h7">Available</Typography>
                  &nbsp;
                  <Typography variant="h4">:</Typography>
                  <DYDXTopLeftAmountContainer>
                    &nbsp;
                    <NumberTypo typoOfIntegers="h6" typoOfDecimals="h8">
                      {String(parseFloat(fix(toDisplayDenomAmount(restakableBaseAmount, DYDX_TOKEN.decimals), 6)))}
                    </NumberTypo>
                  </DYDXTopLeftAmountContainer>
                </DYDXTopLeftContainer>
                <DYDXTopRightContainer>
                  <AmountPresetButton
                    onClick={() => {
                      setCurrentDisplayAmount(trimZeros(times(toDisplayDenomAmount(restakableBaseAmount, DYDX_TOKEN.decimals), '0.25')));
                    }}
                  >
                    <Typography variant="h10">1/4</Typography>
                  </AmountPresetButton>

                  <AmountPresetButton
                    onClick={() => {
                      setCurrentDisplayAmount(trimZeros(times(toDisplayDenomAmount(restakableBaseAmount, DYDX_TOKEN.decimals), '0.5')));
                    }}
                  >
                    <Typography variant="h10">HALF</Typography>
                  </AmountPresetButton>

                  <AmountPresetButton
                    onClick={() => {
                      setCurrentDisplayAmount(trimZeros(toDisplayDenomAmount(restakableBaseAmount, DYDX_TOKEN.decimals)));
                    }}
                  >
                    <Typography variant="h10">MAX</Typography>
                  </AmountPresetButton>
                </DYDXTopRightContainer>
              </DYDXTopContainer>
              <DYDXBodyContainer>
                <TokenContainer>
                  <DYDXIconContainer>
                    <DYDXIcon />
                  </DYDXIconContainer>
                  <TokenTextContainer>
                    <Typography variant="h4">DYDX</Typography>
                    <Base02TextContainer>
                      <Typography variant="h9">dYdX Chain Token</Typography>
                    </Base02TextContainer>
                  </TokenTextContainer>
                </TokenContainer>
                <TokenRightContainer>
                  <StyledInput
                    onChange={(e) => {
                      if (!isDecimal(e.currentTarget.value, DYDX_TOKEN.decimals) && e.currentTarget.value) {
                        return;
                      }

                      setCurrentDisplayAmount(e.currentTarget.value);
                    }}
                    value={currentDisplayAmount}
                    placeholder="0"
                  />
                </TokenRightContainer>
              </DYDXBodyContainer>
            </DYDXContainer>

            <DetailContainer>
              <TxDetails estimatedGasFee={displayEstimateFeeAmount} isOnlyEstimatedGasFee />
            </DetailContainer>
          </ContentsContainer>
          <Tooltip varient="error" title={errorMessage} placement="top" arrow>
            <div>
              <GradientButton
                disabled={!!errorMessage || !redelegateDirectTx}
                onClick={() => {
                  setIsOpenSignConfirmBottomsheet(true);
                }}
              >
                <Typography variant="h4">NEXT</Typography>
              </GradientButton>
            </div>
          </Tooltip>
        </>
      </Container>

      <WrapperContainer data-is-open={isOpenedFromValidatorBottomSheet}>
        <ValidatorBottomSheet
          open={isOpenedFromValidatorBottomSheet}
          onClose={() => setIsOpenedFromValidatorBottomsheet(false)}
          variant="persistent"
          rightText="Staked"
          validatorList={orderedFromValidators}
          onClickValidator={(validator) => {
            setCurrentFromValidatorAddress(validator.operator_address);
            setIsOpenedFromValidatorBottomsheet(false);
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenedToValidatorBottomSheet}>
        <ValidatorBottomSheet
          open={isOpenedToValidatorBottomSheet}
          onClose={() => setIsOpenedToValidatorBottomsheet(false)}
          variant="persistent"
          rightText="Commission"
          validatorList={orderedToValidators}
          onClickValidator={(validator) => {
            setCurrentToValidatorAddress(validator.operator_address);
            setIsOpenedToValidatorBottomsheet(false);
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenSignConfirmBottomsheet}>
        <SignConfirmBottomSheet
          open={isOpenSignConfirmBottomsheet}
          currentValidator={currentFromValidator}
          toValidator={currentToValidator}
          variant="persistent"
          title="Confirm Switch Details"
          descriptionText={`Review your switch validator transaction and click 'Confirm' to proceed.`}
          delegateAmount={debouncedCurrentDisplayAmount}
          displayEstimatedGasFeeAmount={displayEstimateFeeAmount}
          currentMemo={currentMemo}
          isButtonDisabled={isDisabled}
          isEstimation={!simulatedGas && !simulate.error}
          setCurrentMemo={(memo) => {
            setCurrentMemo(memo);
          }}
          transitionDuration={0}
          onClickConfirm={async () => {
            try {
              if (!redelegateDirectTx || !account.data?.account.account_number) return;
              setIsOpenSignConfirmBottomsheet(false);
              setIsOpenTrasactionBottomsheet(true);

              const keypair = mnemonicToPair(currentAccount.mnemonic);
              const response = await signDirectTx(redelegateDirectTx, keypair, account.data.account.account_number);

              restakingClickEvent('Staking Tab', 'Switch-Validator', undefined, {
                'From Validator': currentFromValidator?.description.moniker,
                'To Validator': currentToValidator?.description.moniker,
                'Staked Amount': debouncedCurrentDisplayAmount || '0',
              });

              setTransactionHash(response.tx_response.txhash);

              enqueueSnackbar('Transaction broadcasting success', {
                variant: 'success',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                txHash: response.tx_response.txhash,
                isCosmosTx: true,
              });

              await txInfo.mutate();
            } catch (e) {
              setIsTxTrackingFailed(true);

              enqueueSnackbar('Transaction Failed', {
                variant: 'error',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                errorMsg: '',

                autoHideDuration: 3000,
              });
            }
          }}
          onClose={() => {
            setIsOpenSignConfirmBottomsheet(false);
            setCurrentMemo('');
            setCurrentDisplayAmount('');
            setCurrentFromValidatorAddress('');
            setCurrentToValidatorAddress('');
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenTrasactionBottomsheet}>
        <TransactionBottomSheet
          open={isOpenTrasactionBottomsheet}
          variant="persistent"
          title="Switch Status"
          bodyText={transactionBodyText}
          // subText={transactionSubText}
          // redirectButtonText={transactionRedirectText}
          isFailed={isTxTrackingFailed}
          isSuccess={isTxTrackingSuccess}
          isStaking
          txHash={transactionHash}
          transitionDuration={0}
          onClickRedirect={() => {
            window.open('https://medium.com/cosmostation/dydx-wallet-migration-guide-how-to-manage-your-dydx-tokens-43982ffddfb5');
          }}
          onClose={() => {
            setIsOpenTrasactionBottomsheet(false);
            setIsTxTrackingFailed(false);
            setIsTxTrackingSucces(false);
            setTransactionHash('');
            setCurrentDisplayAmount('');
            setCurrentFromValidatorAddress('');
            setCurrentToValidatorAddress('');
            setCurrentMemo('');
            void delegation.mutate();
            onClose?.({}, 'backdropClick');
          }}
        />
      </WrapperContainer>
    </StyledBottomSheet>
  );
}
